import { useEffect, useRef } from 'react';

import { useLocation, useNavigate } from 'react-router';

const SELECTED_SESSION_ID_STATE_KEY = '__telemostConferencesHistorySelectedSessionId';

interface UseSelectedSessionHistoryParams {
    enabled: boolean;
    selectedSessionId: string | null;
    onSelectedSessionIdChange: (sessionId: string | null) => void;
}

const getLocationState = (state: unknown): Record<string, unknown> => {
    if (state && typeof state === 'object' && !Array.isArray(state)) {
        return state as Record<string, unknown>;
    }

    return {};
};

const getHistorySessionId = (state: unknown): string | null => {
    const sessionId = getLocationState(state)[SELECTED_SESSION_ID_STATE_KEY];

    return typeof sessionId === 'string' ? sessionId : null;
};

export const useSelectedSessionHistory = ({
    enabled,
    selectedSessionId,
    onSelectedSessionIdChange,
}: UseSelectedSessionHistoryParams): void => {
    const location = useLocation();
    const navigate = useNavigate();
    const historySessionId = getHistorySessionId(location.state);
    const previousLocationKeyRef = useRef(location.key);
    const previousSelectedSessionIdRef = useRef(selectedSessionId);
    const historySessionIdRef = useRef(historySessionId);

    useEffect(() => {
        historySessionIdRef.current = historySessionId;

        if (!enabled) {
            previousLocationKeyRef.current = location.key;
            return;
        }

        if (previousLocationKeyRef.current === location.key) {
            return;
        }

        previousLocationKeyRef.current = location.key;

        if (selectedSessionId !== historySessionId) {
            onSelectedSessionIdChange(historySessionId);
        }
    }, [enabled, historySessionId, location.key, onSelectedSessionIdChange, selectedSessionId]);

    useEffect(() => {
        const previousSelectedSessionId = previousSelectedSessionIdRef.current;
        previousSelectedSessionIdRef.current = selectedSessionId;

        if (!enabled || previousSelectedSessionId === selectedSessionId) {
            return;
        }

        const currentHistorySessionId = historySessionIdRef.current;

        if (currentHistorySessionId === selectedSessionId) {
            return;
        }

        if (selectedSessionId) {
            navigate(`${location.pathname}${location.search}${location.hash}`, {
                replace: currentHistorySessionId !== null,
                state: {
                    ...getLocationState(location.state),
                    [SELECTED_SESSION_ID_STATE_KEY]: selectedSessionId,
                },
            });
            return;
        }

        if (currentHistorySessionId !== null) {
            navigate(-1);
        }
    }, [enabled, location.hash, location.pathname, location.search, location.state, navigate, selectedSessionId]);
};
